import axiosClient from '../interceptors';
import { getAPIUrl } from './BaseService';

export interface ClassroomLite {
  id: string;
  name: string;
}

export interface TermLite {
  id: string;
  name: string;
  status?: 'active' | 'completed' | 'archived';
  session_id?: string;
}

export interface SessionLite {
  id: string;
  name: string;
  status?: 'active' | 'completed' | 'archived';
}

export interface SubjectLite {
  id: string;
  name: string;
  classroom_id: string;
  code?: string | null;
  subject_tags?: string[] | null;
  created_at?: string;
  updated_at?: string;
  assigned_teachers?: Array<{
    id: string;
    first_name: string;
    last_name: string;
  }>;
}

export interface ClassroomTeacherDetail {
  id: string;
  classroom_id: string;
  teacher_id: string;
  subject_ids?: string[] | null;
  created_at?: string;
}

export interface ClassroomDetailPayload {
  id: string;
  name: string;
  school_id: string;
  teacher_id?: string | null;
  assigned_teachers: ClassroomTeacherDetail[];
}

export interface GradebookRubricPayload {
  id: string;
  name: string;
  grading_type: string;
  category_weights?: Record<string, number> | null;
  min_passing: number | null;
}

export interface GradebookAssessmentPayload {
  id: string;
  title: string;
  category: string;
  subject_id?: string | null;
  max_score: number;
  weight: number;
}

export interface GradebookPayload {
  classroom_id: string;
  term_id: string;
  students: Array<{ student_id: string; first_name: string; last_name: string }>;
  assessments: GradebookAssessmentPayload[];
  scores: Array<{ student_id: string; assessment_id: string; score_value: number; subject_id?: string | null }>;
  final_grades: Array<{ student_id: string; final_percentage: number; final_grade?: string | number | null }>;
  rubric?: GradebookRubricPayload | null;
  grade_scheme?: Array<{ grade: string; min: number; max: number }> | null;
}

export interface AssessmentCreatePayload {
  title: string;
  description?: string;
  school_id: string;
  subject_id?: string | null;
  classroom_id: string;
  term_id: string;
  category: string;
  max_score: number;
  weight: number;
  due_date?: string;
  allow_late?: boolean;
}

export interface AssessmentPayload extends AssessmentCreatePayload {
  id: string;
  created_at: string;
  updated_at: string;
}

export interface BulkScoreEntry {
  student_id: string;
  assessment_id: string;
  score_value: number | null;
  subject_id?: string | null;
}

export async function listClassrooms(): Promise<ClassroomLite[]> {
  const response = await axiosClient.get<{ items: ClassroomLite[] }>(`${getAPIUrl()}/classrooms`);
  return response.data.items;
}

export async function listTerms(): Promise<TermLite[]> {
  const response = await axiosClient.get<TermLite[]>(`${getAPIUrl()}/terms`);
  return response.data;
}

export async function listSessions(): Promise<SessionLite[]> {
  const response = await axiosClient.get<SessionLite[]>(`${getAPIUrl()}/sessions`);
  return response.data;
}

export async function listSubjects(classroomId: string): Promise<SubjectLite[]> {
  const response = await axiosClient.get<SubjectLite[]>(`${getAPIUrl()}/subjects`, {
    params: { classroom_id: classroomId },
  });
  return response.data;
}

export async function deleteSubject(subjectId: string): Promise<void> {
  await axiosClient.delete(`${getAPIUrl()}/subjects/${subjectId}`);
}

export async function updateSubject(
  subjectId: string,
  payload: { name?: string; code?: string | null; subject_tags?: string[] | null }
): Promise<SubjectLite> {
  const response = await axiosClient.patch<SubjectLite>(
    `${getAPIUrl()}/subjects/${subjectId}`,
    payload,
  );
  return response.data;
}

export async function getClassroomDetail(
  classroomId: string,
): Promise<ClassroomDetailPayload> {
  const response = await axiosClient.get<ClassroomDetailPayload>(
    `${getAPIUrl()}/classrooms/${classroomId}`,
  );
  return response.data;
}

export async function loadGradebook(
  classroomId: string,
  termId: string,
  subjectId?: string,
  rubricId?: string,
): Promise<GradebookPayload> {
  const params: Record<string, string> = {};
  if (subjectId) params.subject_id = subjectId;
  if (rubricId) params.rubric_id = rubricId;

  const response = await axiosClient.get<GradebookPayload>(
    `${getAPIUrl()}/gradebook/${classroomId}/${termId}`,
    { params: Object.keys(params).length ? params : undefined },
  );
  return response.data;
}

export async function exportGradebookCsv(
  classroomId: string,
  termId: string,
  subjectId?: string,
  rubricId?: string,
): Promise<Blob> {
  const params: Record<string, string> = {};
  if (subjectId) params.subject_id = subjectId;
  if (rubricId) params.rubric_id = rubricId;

  const response = await axiosClient.get(
    `${getAPIUrl()}/gradebook/${classroomId}/${termId}/export`,
    { params: Object.keys(params).length ? params : undefined, responseType: 'blob' },
  );
  return response.data as Blob;
}

export interface AssessmentUpdatePayload {
  title?: string;
  category?: string;
  max_score?: number;
  weight?: number;
}

export async function createAssessment(
  payload: AssessmentCreatePayload,
): Promise<AssessmentPayload> {
  const response = await axiosClient.post<AssessmentPayload>(
    `${getAPIUrl()}/assessments`,
    payload,
  );
  return response.data;
}

export async function listAssessments(
  classroomId: string,
  termId: string,
): Promise<AssessmentPayload[]> {
  const response = await axiosClient.get<AssessmentPayload[]>(
    `${getAPIUrl()}/assessments`,
    {
      params: {
        classroom_id: classroomId,
        term_id: termId,
        limit: 500,
      },
    },
  );
  return response.data;
}

export async function updateAssessment(
  assessmentId: string,
  payload: AssessmentUpdatePayload,
): Promise<AssessmentPayload> {
  const response = await axiosClient.patch<AssessmentPayload>(
    `${getAPIUrl()}/assessments/${assessmentId}`,
    payload,
  );
  return response.data;
}

export async function deleteAssessment(assessmentId: string): Promise<void> {
  await axiosClient.delete(`${getAPIUrl()}/assessments/${assessmentId}`);
}

/**
 * Send all pending score changes to the backend in one request.
 * Groups by subject_id and sends one bulk request per subject.
 */
export async function saveScoresBulk(
  classroomId: string,
  termId: string,
  scores: BulkScoreEntry[],
): Promise<void> {
  if (scores.length === 0) return;

  // Group by subject_id
  const bySubject = new Map<string, BulkScoreEntry[]>();
  for (const entry of scores) {
    const sid = entry.subject_id ?? '';
    if (!bySubject.has(sid)) bySubject.set(sid, []);
    bySubject.get(sid)!.push(entry);
  }

  await Promise.all(
    Array.from(bySubject.entries()).map(([subjectId, items]) =>
      axiosClient.post(`${getAPIUrl()}/scores/bulk`, {
        scores: items.map(({ student_id, assessment_id, score_value }) => ({
          student_id,
          assessment_id,
          score_value,
        })),
        classroom_id: classroomId,
        term_id: termId,
        subject_id: subjectId,
        on_conflict: 'update',
      }),
    ),
  );
}
